import { useEffect, useState, useCallback } from "react";
import API from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { RefreshCcw, Repeat, CheckCircle2, PenLine, AlertCircle } from "lucide-react";

const TIER_LABEL = { one_time: "Monthly", subscription: "Bundle Subscribe", royal_90: "Royal Tier" };

/**
 * TierReviewTab — surfaces queued rxs flagged when a patient changed plans.
 * Prescriber decides per-row: keep doses as-is, or rewrite at new tier's ladder.
 */
export default function TierReviewTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [decideOrder, setDecideOrder] = useState(null);
  const [decision, setDecision] = useState("");
  const [newDose, setNewDose] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await API.get("/prescriber/tier-review-queue");
      setRows(data?.rows || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load queue");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openDecide = (o) => {
    setDecideOrder(o);
    setDecision("");
    setNewDose(o.series_dose || "");
    setNotes("");
  };

  const submit = async () => {
    if (!decision) { toast.error("Pick keep or rewrite"); return; }
    if (decision === "rewrite" && !newDose.trim()) {
      toast.error("New dose required when rewriting");
      return;
    }
    setSaving(true);
    try {
      await API.post(`/prescriber/orders/${decideOrder.id}/tier-review`, {
        decision,
        new_dose: decision === "rewrite" ? newDose : null,
        notes: notes || null,
      });
      toast.success(decision === "keep" ? "Doses kept as-is" : "Series rewritten");
      setDecideOrder(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Decision failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="tier-review-tab">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-2" style={{ fontFamily: "'Outfit', sans-serif" }}>
            <Repeat className="w-6 h-6 text-purple-600" /> Pending Tier Review
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
            Queued rxs flagged when a patient changed their plan — decide to keep the existing dose ladder or rewrite it for the new tier.
          </p>
        </div>
        <Button variant="outline" onClick={load} disabled={loading} className="rounded-lg" data-testid="tier-review-refresh">
          <RefreshCcw className="w-4 h-4 mr-1" /> Refresh
        </Button>
      </div>

      {loading ? (
        <p className="text-sm text-slate-500">Loading…</p>
      ) : rows.length === 0 ? (
        <div className="bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-900 rounded-xl p-6 text-center" data-testid="tier-review-empty">
          <CheckCircle2 className="w-8 h-8 mx-auto text-emerald-600 mb-2" />
          <p className="text-sm font-semibold text-emerald-900 dark:text-emerald-100">All caught up — no rxs awaiting tier review.</p>
        </div>
      ) : (
        <ul className="space-y-3" data-testid="tier-review-list">
          {rows.map((o) => (
            <li
              key={o.id}
              className="bg-white dark:bg-slate-900 rounded-xl border border-amber-200 dark:border-amber-900/40 p-4 flex flex-wrap items-center justify-between gap-3"
              data-testid={`tier-review-row-${o.id}`}
            >
              <div className="flex-1 min-w-[200px]">
                <p className="font-semibold text-slate-900 dark:text-white text-sm">
                  {o.patient_name || "(Unknown patient)"}
                  <span className="font-mono text-slate-400 text-xs ml-2">{o.id?.slice(-8)}</span>
                </p>
                <p className="text-xs text-slate-500 mt-0.5">
                  {o.medication_name} <b className="text-amber-700">{o.series_dose}</b> · Month {o.series_position}/{o.total_in_series}
                </p>
                <p className="text-[11px] text-purple-700 dark:text-purple-300 mt-1 flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" />
                  Patient switching to <b className="ml-1">{TIER_LABEL[o.tier_review_target] || o.tier_review_target}</b>
                </p>
              </div>
              <Button
                size="sm"
                onClick={() => openDecide(o)}
                className="rounded-lg bg-purple-600 hover:bg-purple-700 text-white"
                data-testid={`tier-review-decide-${o.id}`}
              >
                Decide
              </Button>
            </li>
          ))}
        </ul>
      )}

      <Dialog open={!!decideOrder} onOpenChange={(v) => { if (!v) setDecideOrder(null); }}>
        <DialogContent className="max-w-md" data-testid="tier-review-modal">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "'Outfit', sans-serif" }}>Tier review decision</DialogTitle>
          </DialogHeader>
          {decideOrder && (
            <div className="space-y-3 py-2">
              <div className="bg-slate-50 dark:bg-slate-800/40 rounded-lg p-3 text-sm">
                <p className="font-semibold">{decideOrder.patient_name}</p>
                <p className="text-xs text-slate-500">
                  {decideOrder.medication_name} · Current dose <b className="text-slate-900 dark:text-white">{decideOrder.series_dose}</b>
                </p>
                <p className="text-[11px] text-purple-600 mt-1">
                  Switching to {TIER_LABEL[decideOrder.tier_review_target] || decideOrder.tier_review_target}
                </p>
              </div>
              <div className="space-y-2">
                <button
                  type="button"
                  onClick={() => setDecision("keep")}
                  className={`w-full text-left rounded-lg border-2 p-3 ${decision === "keep" ? "border-emerald-500 bg-emerald-50" : "border-slate-200 hover:border-emerald-300"}`}
                  data-testid="tier-review-decision-keep"
                >
                  <p className="font-semibold text-sm flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-emerald-600" /> Keep doses as-is
                  </p>
                  <p className="text-xs text-slate-500 mt-1">Dose ladder stays the same. Use when the new tier doesn&apos;t need a different titration.</p>
                </button>
                <button
                  type="button"
                  onClick={() => setDecision("rewrite")}
                  className={`w-full text-left rounded-lg border-2 p-3 ${decision === "rewrite" ? "border-purple-500 bg-purple-50" : "border-slate-200 hover:border-purple-300"}`}
                  data-testid="tier-review-decision-rewrite"
                >
                  <p className="font-semibold text-sm flex items-center gap-2">
                    <PenLine className="w-4 h-4 text-purple-600" /> Rewrite dose
                  </p>
                  <p className="text-xs text-slate-500 mt-1">Replace this rx&apos;s dose. Use when the new tier needs an adjusted ladder.</p>
                </button>
              </div>
              {decision === "rewrite" && (
                <div>
                  <label className="text-xs font-semibold text-slate-700 dark:text-slate-200">New dose</label>
                  <Input
                    value={newDose}
                    onChange={(e) => setNewDose(e.target.value)}
                    placeholder="e.g. 0.5mg"
                    className="mt-1 rounded-lg"
                    data-testid="tier-review-new-dose"
                  />
                </div>
              )}
              <div>
                <label className="text-xs font-semibold text-slate-700 dark:text-slate-200">Notes (optional)</label>
                <Textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={2}
                  placeholder="Optional notes"
                  className="mt-1 rounded-lg"
                  data-testid="tier-review-notes"
                />
              </div>
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDecideOrder(null)} disabled={saving} data-testid="tier-review-cancel">Cancel</Button>
            <Button
              onClick={submit}
              disabled={!decision || saving}
              className="rounded-lg bg-purple-600 hover:bg-purple-700 text-white"
              data-testid="tier-review-submit"
            >
              {saving ? "Saving…" : "Save decision"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
